import React from 'react';
import PropTypes from 'prop-types';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import {
  Field,
  reduxForm,
  SubmissionError,
  getFormSyncErrors,
  formValueSelector
} from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import { withApollo, graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import AuthWrapper from 'components/AuthWrapper';
import AuthTop from 'components/AuthTop';
import AuthBottom from 'components/AuthBottom';
import Button from 'components/Button';
import Input from 'components/Input';
import FormErrors from 'components/FormErrors';
import { setToken } from 'utils/auth';
import validators from 'utils/fieldValidators';
import './LoginForm.css';
import {
  makeSelectAuthRedirect,
  makeSelectLocation,
  makeSelectLocationError
} from 'containers/App/selectors';
import { ERROR_LOGIN_FAILED } from 'utils/messages';
import fragments from 'utils/graphql/fragments';
import { clearSessionError } from 'containers/App/actions';
import { setLegacyCookie } from 'utils/cookies';
import { parseQuery } from 'utils/functions';
import ViewablePasswordField from 'components/ViewablePasswordField';
import { Link } from 'react-router-dom';
import { GLOBAL_MESSAGES, SIGN_UP_URL } from 'utils/constants';
import { getMKeyCookie } from 'utils/cookies';

function LoginForm({
  handleSubmit,
  error,
  submitting,
  authError,
  formSyncErrors,
  formValues,
  location,
  submitFailed
}) {
  const params = parseQuery(location.search);
  let queryErrors = [];

  if (params.error) {
    const error = params.error;

    if (error === 'signup_user_exists') {
      if (params.display_name && params.email) {
        queryErrors.push(
          `Dear ${params.display_name}, it seems you already have an active account. Please login with your existing account: ${params.email}`
        );
      }
    }
  }

  let queryMessages = [];

  if (params.message) {
    const message = params.message;

    if (GLOBAL_MESSAGES[message]) {
      queryMessages.push(GLOBAL_MESSAGES[message]);
    }
  }

  return (
    <AuthWrapper>
      <form onSubmit={handleSubmit} className="login-form">
        <AuthTop>
          <div className="login-top">
            {authError && <FormErrors errors={[authError.message]} />}
            {formSyncErrors &&
              submitFailed && (
                <FormErrors
                  errors={Object.values(formSyncErrors)}
                  showErrors={
                    (!!formValues.email && formValues.email.length > 0) ||
                    (!!formValues.password && formValues.password.length > 0)
                  }
                />
              )}
            {error && !submitting && <FormErrors errors={error} />}
            {queryErrors && <FormErrors errors={queryErrors} />}
            {!submitFailed &&
              queryMessages && <FormErrors errors={queryMessages} />}
            <Field
              name="email"
              component={Input}
              type="email"
              label="Email Address"
              readOnly={submitting}
              validate={[validators.emailRequired, validators.email]}
              autoFocus
              hideErrors
              hideWarnings
            />
            <Field
              name="password"
              component={ViewablePasswordField}
              type="password"
              label="Password"
              readOnly={submitting}
              validate={validators.passwordRequired}
              hideErrors
              hideWarnings
            />
          </div>
        </AuthTop>
        <AuthBottom>
          <div className="login-bottom">
            <Button
              className="btn btn-success btn-block btn-square"
              disabled={submitting}
            >
              {!submitting && <span>Sign in</span>}
              {submitting && <span>Signing in...</span>}
            </Button>
            <Link className="link" to="/forgot-password">
              Forgot Password
            </Link>
            <div className="signup">
              <span className="label">Don&apos;t have an account yet?</span>
              <a className="link" href={SIGN_UP_URL}>
                Sign up
              </a>
            </div>
          </div>
        </AuthBottom>
      </form>
    </AuthWrapper>
  );
}

// These prop types are passed down from the reduxForm HOC.
LoginForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired, // handleSubmit is mapped to the onSubmit prop via reduxForm.
  error: PropTypes.arrayOf(PropTypes.string),
  submitting: PropTypes.bool,
  invalid: PropTypes.bool
};

// Create redux form HOC.
const withReduxForm = reduxForm({
  form: 'loginForm' // Unique name for the form.
});

// GraphQL query for submitting login and retrieving token and user data.
const myLogin = gql`
  mutation MyLogin($userCredentials: UserCredentials!) {
    login(userCredentials: $userCredentials) {
      sessionToken
      legacySessionToken
      user {
        ...UserFields
      }
    }
  }
  ${fragments.user}
`;

// Create graphql HOC.
const withData = graphql(myLogin, {
  props: ({ ownProps, mutate }) => {
    return {
      authError: ownProps.authError,
      formSyncErrors: ownProps.formSyncErrors,
      formValues: ownProps.formValues,
      onSubmit: (values, dispatch) => {
        dispatch(clearSessionError());

        return mutate({ variables: { userCredentials: values.toJS() } })
          .then(({ data }) => {
            setToken(data.login.sessionToken);
            ownProps.client.recreateWebSocketConnection();

            setLegacyCookie(data.login.legacySessionToken);

            if (getMKeyCookie()) {
              dispatch(push('/mobile/games'));
            } else if (
              ownProps.authRedirect &&
              ownProps.authRedirect.pathname
            ) {
              // Redirect back to referrer
              // TODO: Doesn't look like this is currently working during TOS
              // investigation.
              // Is it still a requirement?
              dispatch(push(ownProps.authRedirect.pathname));
            } else {
              dispatch(push('/'));
            }

            return data;
          })
          .catch(({ graphQLErrors, message }) => {
            // If there are errors included in the response data related to the query, we will show all of them.
            if (graphQLErrors) {
              throw new SubmissionError({ _error: [ERROR_LOGIN_FAILED] });
            }

            // Default to simply showing the message from the error object itself.
            throw new SubmissionError({ _error: [message] });
          });
      }
    };
  }
});

const selector = formValueSelector('loginForm');

const mapStateToProps = createStructuredSelector({
  authRedirect: makeSelectAuthRedirect(),
  authError: makeSelectLocationError(),
  formSyncErrors: getFormSyncErrors('loginForm'),
  formValues: state => selector(state, 'email', 'password'),
  location: makeSelectLocation()
});

const withConnect = connect(mapStateToProps);

// Compose redux form and graphql HOCs together.
export default withApollo(
  compose(withConnect, withData, withReduxForm)(LoginForm)
);



// WEBPACK FOOTER //
// ./src/containers/LoginForm/index.js